/**
 * CreateCustomInvoiceModal
 * Modal for creating custom invoices (not linked to any affaire)
 * Used for one-time customers or services outside of normal affaire workflow
 * Styled to match GenerateSingleInvoiceModal
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  DocumentTextIcon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  PaperAirplaneIcon,
  EyeIcon,
  MinusIcon,
} from '@heroicons/react/24/outline';
import { useCreateCustomInvoice, useValidateInvoice } from '../../hooks/useInvoices';
import { downloadInvoicePdf } from '../../api/facturation.api';
import type { CustomInvoiceCustomer, CustomInvoiceLine, BelgianVatRate, InvoiceLanguage, Invoice } from '../../types/facturation.types';
import { validateEmail } from '../../utils/validation';
import { useTranslation } from '@/i18n/useTranslation';
import { PdfPreviewModal } from '../PdfPreviewModal/PdfPreviewModal';
import { COUNTRY_OPTIONS } from '../../constants/countries.constants';
import '../../facturation.css';

// Prefill data for customer (from billing contact)
interface PrefillCustomer {
  name?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  countryCode?: string;
  email?: string;
  vatNumber?: string;
}

interface CreateCustomInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Optional prefill data from affaire billing contact */
  prefillCustomer?: PrefillCustomer;
  /** Optional: link invoice to an affaire (displays affaire info in PDF) */
  affaireId?: string;
}

// Format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
};

// Language options
const languages = [
  { code: 'fr', label: 'Francais' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
];

// VAT rate options
const vatRateOptions: { rate: BelgianVatRate; label: string }[] = [
  { rate: 21, label: '21%' },
  { rate: 12, label: '12%' },
  { rate: 6, label: '6%' },
  { rate: 0, label: '0%' },
];

// Line item for the form
interface LineItemForm {
  id: string;
  description: string;
  quantity: number;
  unitPriceHtva: number;
  unitPriceTvac: number;
  tvaRate: BelgianVatRate;
}

const tvaMultiplier = (rate: BelgianVatRate) => 1 + rate / 100;
const round2 = (n: number) => Math.round(n * 100) / 100;

// Generate unique id for line items
const generateId = () => Math.random().toString(36).substring(2, 9);

export const CreateCustomInvoiceModal: React.FC<CreateCustomInvoiceModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  prefillCustomer,
  affaireId,
}) => {
  const { t } = useTranslation();

  // Helper to get initial customer state (with optional prefill)
  const getInitialCustomer = useCallback((): CustomInvoiceCustomer => ({
    name: prefillCustomer?.name || '',
    address: prefillCustomer?.address || '',
    postalCode: prefillCustomer?.postalCode || '',
    city: prefillCustomer?.city || '',
    countryCode: prefillCustomer?.countryCode || 'BE',
    email: prefillCustomer?.email || '',
    vatNumber: prefillCustomer?.vatNumber || '',
  }), [prefillCustomer]);

  // Step state (simplified: no more 'preview' step)
  const [step, setStep] = useState<'customer' | 'lines' | 'generating' | 'generated' | 'validating' | 'success' | 'error'>('customer');

  // Customer form
  const [customer, setCustomer] = useState<CustomInvoiceCustomer>(getInitialCustomer);

  // Invoice options
  const [title, setTitle] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState<InvoiceLanguage>('fr');

  // Line items
  const [lines, setLines] = useState<LineItemForm[]>([
    { id: generateId(), description: '', quantity: 1, unitPriceHtva: 0, unitPriceTvac: 0, tvaRate: 21 },
  ]);

  // Generated invoice
  const [generatedInvoice, setGeneratedInvoice] = useState<Invoice | null>(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [sendEmailOnValidate, setSendEmailOnValidate] = useState(true);

  // Errors
  const [errorMessage, setErrorMessage] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  // Mutations
  const createMutation = useCreateCustomInvoice();
  const validateMutation = useValidateInvoice();

  // Update customer when prefillCustomer changes (e.g., when modal opens with new affaire)
  React.useEffect(() => {
    if (isOpen && prefillCustomer) {
      setCustomer(getInitialCustomer());
    }
  }, [isOpen, prefillCustomer, getInitialCustomer]);

  // Reset form when modal opens/closes
  const handleClose = useCallback(() => {
    setStep('customer');
    setCustomer(getInitialCustomer());
    setTitle('');
    setSelectedLanguage('fr');
    setLines([{ id: generateId(), description: '', quantity: 1, unitPriceHtva: 0, unitPriceTvac: 0, tvaRate: 21 }]);
    setGeneratedInvoice(null);
    setShowPdfPreview(false);
    setErrorMessage('');
    setEmailError(null);
    setSendEmailOnValidate(true);
    onClose();
  }, [onClose, getInitialCustomer]);

  // Calculate totals
  const totals = useMemo(() => {
    let totalHtva = 0;
    let totalTva = 0;

    lines.forEach((line) => {
      const lineHtva = line.quantity * line.unitPriceHtva;
      const lineTva = lineHtva * (line.tvaRate / 100);
      totalHtva += lineHtva;
      totalTva += lineTva;
    });

    return {
      totalHtva,
      totalTva,
      totalTvac: totalHtva + totalTva,
    };
  }, [lines]);

  // Validate customer step
  const validateCustomerStep = (): boolean => {
    if (!customer.name.trim()) {
      setErrorMessage(t('facturation.customInvoice.validation.nameRequired', 'Le nom du client est requis'));
      return false;
    }
    // Email is mandatory for custom invoices
    if (!customer.email?.trim()) {
      setEmailError('L\'email du client est requis');
      return false;
    }
    const validation = validateEmail(customer.email.trim(), false);
    if (!validation.valid) {
      setEmailError(validation.error || 'Email invalide');
      return false;
    }
    setErrorMessage('');
    setEmailError(null);
    return true;
  };

  // Validate lines step
  const validateLinesStep = (): boolean => {
    const validLines = lines.filter(
      (line) => line.description.trim() && line.quantity > 0 && line.unitPriceHtva > 0
    );
    if (validLines.length === 0) {
      setErrorMessage(t('facturation.customInvoice.validation.atLeastOneLine', 'Au moins une ligne est requise'));
      return false;
    }
    const incompleteLines = lines.filter(
      (line) =>
        (line.description.trim() || line.unitPriceHtva > 0) &&
        (!line.description.trim() || line.unitPriceHtva <= 0)
    );
    if (incompleteLines.length > 0) {
      setErrorMessage(t('facturation.customInvoice.validation.incompleteLines', 'Veuillez completer toutes les lignes commencees'));
      return false;
    }
    setErrorMessage('');
    return true;
  };

  // Add new line
  const handleAddLine = () => {
    setLines([...lines, { id: generateId(), description: '', quantity: 1, unitPriceHtva: 0, unitPriceTvac: 0, tvaRate: 21 }]);
  };

  // Remove line
  const handleRemoveLine = (id: string) => {
    if (lines.length > 1) {
      setLines(lines.filter((line) => line.id !== id));
    }
  };

  // Update line
  const handleUpdateLine = (id: string, field: keyof LineItemForm, value: string | number) => {
    setLines(
      lines.map((line) =>
        line.id === id ? { ...line, [field]: value } : line
      )
    );
  };

  // HTVA -> TVAC sync (HTVA is source of truth, full precision)
  const handleLineHtvaChange = (id: string, htva: number) => {
    setLines(lines.map((line) =>
      line.id === id
        ? { ...line, unitPriceHtva: htva, unitPriceTvac: round2(htva * tvaMultiplier(line.tvaRate)) }
        : line
    ));
  };

  // TVAC -> HTVA back-calculation (keep HTVA full precision so TVAC round-trips exactly)
  const handleLineTvacChange = (id: string, tvac: number) => {
    setLines(lines.map((line) =>
      line.id === id
        ? { ...line, unitPriceTvac: tvac, unitPriceHtva: tvac / tvaMultiplier(line.tvaRate) }
        : line
    ));
  };

  // VAT rate change re-derives TVAC from the (authoritative) HTVA
  const handleLineRateChange = (id: string, rate: BelgianVatRate) => {
    setLines(lines.map((line) =>
      line.id === id
        ? { ...line, tvaRate: rate, unitPriceTvac: round2(line.unitPriceHtva * tvaMultiplier(rate)) }
        : line
    ));
  };

  // Handle next step
  const handleNext = () => {
    if (step === 'customer') {
      if (validateCustomerStep()) {
        setStep('lines');
      }
    } else if (step === 'lines') {
      if (validateLinesStep()) {
        // Skip preview step and generate directly
        handleGenerate();
      }
    }
  };

  // Handle back
  const handleBack = () => {
    setErrorMessage('');
    if (step === 'lines') {
      setStep('customer');
    } else if (step === 'generated') {
      // Go back to lines step (skip preview step)
      setStep('lines');
    }
  };

  // Generate invoice
  const handleGenerate = async () => {
    setStep('generating');
    setErrorMessage('');

    try {
      const validLines: CustomInvoiceLine[] = lines
        .filter((line) => line.description.trim() && line.quantity > 0 && line.unitPriceHtva > 0)
        .map((line) => ({
          description: line.description.trim(),
          quantity: line.quantity,
          unitPriceHtva: line.unitPriceHtva,
          tvaRate: line.tvaRate,
        }));

      const result = await createMutation.mutateAsync({
        title: title.trim() || undefined,
        customer: {
          name: customer.name.trim(),
          address: customer.address?.trim() || undefined,
          postalCode: customer.postalCode?.trim() || undefined,
          city: customer.city?.trim() || undefined,
          countryCode: customer.countryCode?.trim() || undefined,
          email: customer.email?.trim() || undefined,
          vatNumber: customer.vatNumber?.trim() || undefined,
        },
        language: selectedLanguage,
        lines: validLines,
        affaireId: affaireId || undefined,
      });

      setGeneratedInvoice(result);
      setStep('generated');
    } catch (error) {
      console.error('Failed to generate invoice:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Erreur lors de la generation');
      setStep('error');
    }
  };

  // Validate and send invoice
  const handleValidate = async () => {
    if (!generatedInvoice) return;

    setStep('validating');
    setErrorMessage('');

    try {
      // Only send email if checkbox is checked AND customer has an email
      const shouldSendEmail = sendEmailOnValidate && !!customer.email?.trim();
      await validateMutation.mutateAsync({
        id: generatedInvoice.id,
        currentStatus: 'DRAFT',
        sendEmail: shouldSendEmail,
      });

      setStep('success');
      onSuccess?.();
    } catch (error) {
      console.error('Failed to validate invoice:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Erreur lors de la validation');
      setStep('error');
    }
  };

  // Handle PDF download
  const handleDownloadPdf = async () => {
    if (!generatedInvoice) return;

    setIsDownloading(true);
    try {
      const blob = await downloadInvoicePdf(generatedInvoice.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = generatedInvoice.invoice_number
        ? `facture_${generatedInvoice.invoice_number.replace(/\//g, '-')}.pdf`
        : `facture_brouillon_${generatedInvoice.id}.pdf`;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  // Get step title
  const getStepTitle = () => {
    switch (step) {
      case 'customer':
        return 'Client';
      case 'lines':
        return 'Produits';
      case 'generating':
        return 'Generation en cours...';
      case 'generated':
        return 'Verifier la facture';
      case 'validating':
        return 'Envoi en cours...';
      case 'success':
        return 'Facture envoyee';
      case 'error':
        return 'Erreur';
      default:
        return 'Facture personnalisee';
    }
  };

  // Get step subtitle
  const getStepSubtitle = () => {
    switch (step) {
      case 'customer':
        return 'Etape 1/2 - Informations client';
      case 'lines':
        return 'Etape 2/2 - Produits et services';
      case 'generated':
        return generatedInvoice?.invoice_number || 'Brouillon';
      default:
        return 'Facture sans affaire';
    }
  };

  return (
    <>
      <Transition show={isOpen} as={React.Fragment}>
        <Dialog onClose={handleClose} className="relative z-50">
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-4xl max-h-[90vh] transform rounded-2xl bg-white shadow-xl transition-all flex flex-col">
                  {/* Header */}
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                      <DocumentTextIcon className="h-6 w-6 text-primary" />
                      <div>
                        <Dialog.Title className="text-lg font-semibold text-gray-900">
                          {getStepTitle()}
                        </Dialog.Title>
                        <p className="text-sm text-gray-500">
                          {getStepSubtitle()}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleClose}
                      className="facturation-btn-modal-close"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Content - scrollable */}
                  <div className="p-6 overflow-y-auto flex-1">
                    {/* Error message */}
                    {errorMessage && !['error', 'success'].includes(step) && (
                      <div className="bg-red-50 rounded-lg p-4 mb-6">
                        <div className="flex items-start gap-3">
                          <ExclamationTriangleIcon className="h-6 w-6 text-red-600 flex-shrink-0" />
                          <div>
                            <p className="text-sm text-red-700">{errorMessage}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step: Customer */}
                    {step === 'customer' && (
                      <div className="space-y-6">
                        {/* Title */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Titre de la facture <span className="text-gray-400 font-normal">(optionnel)</span>
                          </label>
                          <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                            placeholder="ex: Consultation, Formation..."
                          />
                        </div>

                        {/* Customer name */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nom du client <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={customer.name}
                            onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                            placeholder="Nom ou raison sociale"
                            required
                          />
                        </div>

                        {/* Address - street */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Adresse (rue et numero)
                          </label>
                          <textarea
                            value={customer.address || ''}
                            onChange={(e) => setCustomer({ ...customer, address: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                            rows={2}
                            placeholder="Rue, numero"
                          />
                        </div>

                        {/* Address - postal code / city / country */}
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Code postal
                            </label>
                            <input
                              type="text"
                              value={customer.postalCode || ''}
                              onChange={(e) => setCustomer({ ...customer, postalCode: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                              placeholder="1000"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Ville
                            </label>
                            <input
                              type="text"
                              value={customer.city || ''}
                              onChange={(e) => setCustomer({ ...customer, city: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                              placeholder="Bruxelles"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Pays
                            </label>
                            <select
                              value={customer.countryCode || 'BE'}
                              onChange={(e) => setCustomer({ ...customer, countryCode: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                            >
                              {COUNTRY_OPTIONS.map((c) => (
                                <option key={c.code} value={c.code}>{c.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          {/* Email */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Email <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="email"
                              value={customer.email || ''}
                              onChange={(e) => {
                                setCustomer({ ...customer, email: e.target.value });
                                setEmailError(null);
                              }}
                              onBlur={(e) => {
                                const email = e.target.value.trim();
                                if (email) {
                                  const validation = validateEmail(email, false);
                                  setEmailError(validation.error || null);
                                }
                              }}
                              className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary ${
                                emailError ? 'border-red-500' : 'border-gray-300'
                              }`}
                              placeholder="email@exemple.com"
                              required
                            />
                            {emailError && <p className="mt-1 text-xs text-red-600">{emailError}</p>}
                          </div>

                          {/* VAT number */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Numero TVA
                            </label>
                            <input
                              type="text"
                              value={customer.vatNumber || ''}
                              onChange={(e) => setCustomer({ ...customer, vatNumber: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                              placeholder="BE0123456789"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step: Lines */}
                    {step === 'lines' && (
                      <div className="space-y-6">
                        {/* Products list */}
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-3">Produits / Services</h4>
                          <div className="space-y-3">
                            {lines.map((line) => {
                              const lineTotal = line.quantity * line.unitPriceHtva;
                              return (
                                <div
                                  key={line.id}
                                  className={`p-3 rounded-lg bg-white border ${
                                    line.description && line.unitPriceHtva > 0
                                      ? 'border-primary/20 bg-primary/5'
                                      : 'border-gray-200'
                                  }`}
                                >
                                  <div className="flex items-end gap-3">
                                    {/* Description */}
                                    <div className="flex-1">
                                      <label className="block text-[10px] text-gray-500 mb-0.5 uppercase tracking-wide">Description</label>
                                      <input
                                        type="text"
                                        value={line.description}
                                        onChange={(e) => handleUpdateLine(line.id, 'description', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                                        placeholder="Description du produit/service"
                                      />
                                    </div>
                                    {/* Quantity controls */}
                                    <div>
                                      <label className="block text-[10px] text-gray-500 mb-0.5 uppercase tracking-wide">Qté</label>
                                      <div className="flex items-center border border-gray-300 rounded-lg">
                                      <button
                                        type="button"
                                        onClick={() => handleUpdateLine(line.id, 'quantity', Math.max(1, line.quantity - 1))}
                                        disabled={line.quantity <= 1}
                                        className="p-1.5 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed rounded-l-lg"
                                      >
                                        <MinusIcon className="h-4 w-4" />
                                      </button>
                                      <input
                                        type="number"
                                        value={line.quantity}
                                        onChange={(e) => handleUpdateLine(line.id, 'quantity', Math.max(1, parseInt(e.target.value) || 1))}
                                        min={1}
                                        className="w-12 text-center text-sm border-x border-gray-300 py-1.5 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => handleUpdateLine(line.id, 'quantity', line.quantity + 1)}
                                        className="p-1.5 hover:bg-gray-100 rounded-r-lg"
                                      >
                                        <PlusIcon className="h-4 w-4" />
                                      </button>
                                      </div>
                                    </div>
                                    {/* Price HTVA */}
                                    <div className="w-24">
                                      <label className="block text-[10px] text-gray-500 mb-0.5 text-right uppercase tracking-wide">HTVA</label>
                                      <input
                                        type="number"
                                        value={line.unitPriceHtva || ''}
                                        onChange={(e) => handleLineHtvaChange(line.id, parseFloat(e.target.value) || 0)}
                                        min={0}
                                        step={0.01}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right focus:ring-2 focus:ring-primary focus:border-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        placeholder="0.00"
                                      />
                                    </div>
                                    {/* Price TVAC */}
                                    <div className="w-24">
                                      <label className="block text-[10px] text-gray-500 mb-0.5 text-right uppercase tracking-wide">TVAC</label>
                                      <input
                                        type="number"
                                        value={line.unitPriceTvac || ''}
                                        onChange={(e) => handleLineTvacChange(line.id, parseFloat(e.target.value) || 0)}
                                        min={0}
                                        step={0.01}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right focus:ring-2 focus:ring-primary focus:border-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        placeholder="0.00"
                                      />
                                    </div>
                                    {/* VAT rate */}
                                    <div className="w-20">
                                      <label className="block text-[10px] text-gray-500 mb-0.5 text-center uppercase tracking-wide">TVA</label>
                                      <select
                                        value={line.tvaRate}
                                        onChange={(e) => handleLineRateChange(line.id, parseInt(e.target.value) as BelgianVatRate)}
                                        className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                                      >
                                        {vatRateOptions.map((opt) => (
                                          <option key={opt.rate} value={opt.rate}>
                                            {opt.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                    {/* Line total */}
                                    <div className="w-24 text-sm font-medium text-primary text-right">
                                      <span className="block text-[10px] text-gray-500 mb-0.5 uppercase tracking-wide font-normal">Total</span>
                                      {formatCurrency(lineTotal)}
                                    </div>
                                    {/* Remove button */}
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveLine(line.id)}
                                      disabled={lines.length <= 1}
                                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                                      title="Supprimer"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {/* Add line button */}
                          <button
                            type="button"
                            onClick={handleAddLine}
                            className="mt-3 inline-flex items-center gap-2 px-3 py-2 text-sm text-primary hover:bg-primary/10 rounded-lg transition-colors"
                          >
                            <PlusIcon className="h-4 w-4" />
                            Ajouter une ligne
                          </button>
                        </div>

                        {/* Billing summary */}
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Total HTVA:</span>
                              <span>{formatCurrency(totals.totalHtva)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">TVA:</span>
                              <span>{formatCurrency(totals.totalTva)}</span>
                            </div>
                            <div className="flex justify-between font-semibold border-t pt-2">
                              <span>Total TVAC:</span>
                              <span className="text-primary">{formatCurrency(totals.totalTvac)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step: Generating */}
                    {step === 'generating' && (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                        <p className="mt-4 text-gray-600">Generation de la facture...</p>
                        <p className="text-sm text-gray-500">Veuillez patienter</p>
                      </div>
                    )}

                    {/* Step: Generated (draft preview) */}
                    {step === 'generated' && generatedInvoice && (
                      <div className="space-y-6">
                        {/* Draft warning */}
                        <div className="fact-draft-warning">
                          <p>
                            <strong>Brouillon</strong> - Le numero de facture sera attribue lors de la validation.
                          </p>
                        </div>

                        <div className="bg-green-50 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
                            <div>
                              <h3 className="font-medium text-green-900">Brouillon cree</h3>
                              <p className="text-sm text-green-700 mt-1">
                                Le brouillon a ete cree. Cliquez sur "Valider" pour attribuer un numero et envoyer a Falco.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Invoice summary */}
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-3">Resume</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Numero:</span>
                              <span className="font-mono font-medium">{generatedInvoice.invoice_number || 'Brouillon'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Client:</span>
                              <span>{generatedInvoice.billing_name}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Montant HTVA:</span>
                              <span>{formatCurrency(generatedInvoice.total_htva)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">TVA:</span>
                              <span>{formatCurrency(generatedInvoice.total_tva)}</span>
                            </div>
                            <div className="flex justify-between font-medium text-gray-900 border-t pt-2">
                              <span>Total TVAC:</span>
                              <span className="text-primary">{formatCurrency(generatedInvoice.total_tvac)}</span>
                            </div>
                          </div>
                        </div>

                        {/* Email option */}
                        {customer.email && (
                          <div className="bg-gray-50 rounded-lg p-4">
                            <label className="flex items-center gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={sendEmailOnValidate}
                                onChange={(e) => setSendEmailOnValidate(e.target.checked)}
                                className="fact-email-checkbox"
                              />
                              <span className="text-sm text-gray-700">
                                Envoyer la facture par email au client
                              </span>
                            </label>
                            <p className="text-xs text-gray-500 mt-1 ml-7">
                              Email: {customer.email}
                            </p>
                          </div>
                        )}

                        {/* PDF preview button */}
                        <div className="flex justify-center">
                          <button
                            onClick={() => setShowPdfPreview(true)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 transition-colors"
                          >
                            <EyeIcon className="h-5 w-5" />
                            Previsualiser le brouillon
                          </button>
                        </div>

                        {/* Next step info */}
                        <div className="bg-blue-50 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <PaperAirplaneIcon className="h-6 w-6 text-blue-600 flex-shrink-0" />
                            <div>
                              <h3 className="font-medium text-blue-900">Prochaine etape</h3>
                              <p className="text-sm text-blue-700 mt-1">
                                Verifiez le PDF puis cliquez sur "Valider et envoyer" pour transmettre la facture a Falco.
                                {customer.email && sendEmailOnValidate && (
                                  <span className="block mt-1">
                                    La facture sera egalement envoyee par email a <span className="font-medium">{customer.email}</span>.
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step: Validating */}
                    {step === 'validating' && (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                        <p className="mt-4 text-gray-600">Envoi de la facture a Falco...</p>
                      </div>
                    )}

                    {/* Step: Success */}
                    {step === 'success' && (
                      <div className="text-center py-8">
                        <CheckCircleIcon className="h-12 w-12 text-green-600 mx-auto" />
                        <h3 className="mt-4 text-lg font-semibold text-gray-900">Facture envoyee</h3>
                        <p className="mt-2 text-gray-600">
                          La facture a ete envoyee a Falco avec succes.
                        </p>
                        <div className="fact-success-actions">
                          {generatedInvoice?.pdf_path && (
                            <button
                              onClick={handleDownloadPdf}
                              disabled={isDownloading}
                              className="fact-success-download-btn"
                            >
                              {isDownloading ? (
                                <>
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                  Telechargement...
                                </>
                              ) : (
                                <>
                                  <ArrowDownTrayIcon className="h-5 w-5" />
                                  Telecharger le PDF
                                </>
                              )}
                            </button>
                          )}
                          <button
                            onClick={handleClose}
                            className="fact-success-close-btn"
                          >
                            Fermer
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Step: Error */}
                    {step === 'error' && (
                      <div className="text-center py-8">
                        <ExclamationTriangleIcon className="h-12 w-12 text-red-600 mx-auto" />
                        <h3 className="mt-4 text-lg font-semibold text-gray-900">Erreur</h3>
                        <p className="mt-2 text-red-600">{errorMessage}</p>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between flex-shrink-0">
                    {/* Language selection - only show in first 2 steps */}
                    {['customer', 'lines'].includes(step) && (
                      <div className="flex items-center gap-1">
                        {languages.map((lang) => (
                          <button
                            key={lang.code}
                            type="button"
                            onClick={() => setSelectedLanguage(lang.code as InvoiceLanguage)}
                            className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                              selectedLanguage === lang.code
                                ? 'bg-[#ef5343] text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {lang.code.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    )}
                    {['generating', 'generated', 'validating', 'success', 'error'].includes(step) && <div />}

                    {/* Action buttons */}
                    <div className="flex gap-3">
                      {step === 'customer' && (
                        <>
                          <button onClick={handleClose} className="facturation-btn-modal-cancel">
                            Annuler
                          </button>
                          <button onClick={handleNext} className="facturation-btn-modal-primary">
                            Suivant
                          </button>
                        </>
                      )}
                      {step === 'lines' && (
                        <>
                          <button onClick={handleBack} className="facturation-btn-modal-cancel">
                            Retour
                          </button>
                          <button onClick={handleNext} className="facturation-btn-modal-primary">
                            Generer la facture
                          </button>
                        </>
                      )}
                      {step === 'generated' && (
                        <>
                          <button onClick={handleClose} className="facturation-btn-modal-cancel">
                            Fermer
                          </button>
                          <button
                            onClick={handleValidate}
                            className="facturation-btn-modal-primary inline-flex items-center gap-2"
                          >
                            <PaperAirplaneIcon className="h-4 w-4" />
                            Valider et envoyer a Falco
                          </button>
                        </>
                      )}
                      {(step === 'success' || step === 'error') && (
                        <button onClick={handleClose} className="facturation-btn-modal-primary">
                          Fermer
                        </button>
                      )}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* PDF Preview Modal */}
      {showPdfPreview && generatedInvoice && (
        <PdfPreviewModal
          document={{
            type: 'invoice',
            id: generatedInvoice.id,
            number: generatedInvoice.invoice_number || 'Brouillon',
            date: generatedInvoice.invoice_date,
            clientName: generatedInvoice.billing_name || '-',
            amount: generatedInvoice.total_tvac,
            status: generatedInvoice.status,
            hasPdf: true,
          }}
          onClose={() => setShowPdfPreview(false)}
          onDownload={handleDownloadPdf}
          previewMode={true}
          onBack={() => setShowPdfPreview(false)}
          backLabel="Retour a la validation"
        />
      )}
    </>
  );
};

export default CreateCustomInvoiceModal;
